import { useCallback, useState } from 'preact/hooks';
import { parseQty, parseTaka, type Milli, type Paisa } from '@khata/core';
import { ApiError, OfflineError } from './api.js';
import { t, toLatinDigits, translateError } from './i18n.js';
import { closeSheet, showToast } from './ui.js';

export interface FormError {
  readonly message: string;
  readonly field?: string;
}

/** A tiny string-field form: every input is a string until save. */
export function useFields<T extends Record<string, string>>(
  initial: T,
): [T, (k: keyof T, v: string) => void, (next: T) => void] {
  const [f, setF] = useState<T>(initial);
  const set = useCallback((k: keyof T, v: string) => setF((prev) => ({ ...prev, [k]: v })), []);
  return [f, set, setF];
}

/** Money text (Latin or Bangla digits) → paisa, or an error for the field. */
export function money(
  value: string,
  field: string,
  opts: { min?: number; allowEmpty?: boolean } = {},
): Paisa | FormError {
  const s = toLatinDigits(value.trim());
  if (s === '') {
    if (opts.allowEmpty) return 0 as Paisa;
    return { message: t('form_enterAmount'), field };
  }
  const p = parseTaka(s);
  if (p === null) return { message: t('form_invalidAmount'), field };
  if (p < (opts.min ?? 0))
    return {
      message: opts.min && opts.min > 0 ? t('form_enterAmount') : t('form_negativeAmount'),
      field,
    };
  return p;
}

/** Quantity text (Latin or Bangla digits) → milli-units, or an error for the field. */
export function quantity(
  value: string,
  field: string,
  opts: { min?: number } = {},
): Milli | FormError {
  const s = toLatinDigits(value.trim());
  if (s === '') return { message: t('form_enterQty'), field };
  const q = parseQty(s);
  if (q === null) return { message: t('form_invalidQty'), field };
  if (q < (opts.min ?? 1)) return { message: t('form_enterQty'), field };
  return q;
}

export const isErr = (v: unknown): v is FormError =>
  typeof v === 'object' && v !== null && 'message' in v;

/** Turn a core/server error into a form error in the current language. */
export function fromApi(
  error: {
    code: string;
    message: string;
    field?: string;
    params?: Readonly<Record<string, string | number>>;
  },
  context?: 'sale' | 'purchase',
): FormError {
  const message = translateError(error, context);
  return error.field === undefined ? { message } : { message, field: error.field };
}

/** Wrap a save handler: shows validation errors, closes the sheet and toasts on success. */
export function useSave(): [
  FormError | null,
  boolean,
  (fn: () => Promise<FormError | string | null>) => Promise<void>,
  (e: FormError | null) => void,
] {
  const [error, setError] = useState<FormError | null>(null);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (fn: () => Promise<FormError | string | null>) => {
    setError(null);
    setBusy(true);
    try {
      const r = await fn();
      if (r === null) return;
      if (typeof r === 'string') {
        closeSheet();
        showToast(r);
      } else setError(r);
    } catch (e) {
      if (e instanceof OfflineError) setError({ message: t('form_offline') });
      else if (e instanceof ApiError) setError(fromApi(e.body));
      else {
        console.error(e);
        setError({ message: t('form_failed') });
      }
    } finally {
      setBusy(false);
    }
  }, []);
  return [error, busy, run, setError];
}
